/* eslint-disable */
import React, { useState, useEffect, useMemo } from "react";
import { useSelector } from "react-redux";
import Sidebar from "../../components/sidebar/Sidebar2";
import Navbar from "../../components/navbar/Navbar2";
import Footer from "../../components/footer/Footer";
import FoorchatDashboard from "./foorchatDashboard";
import { serverTimestamp, doc, updateDoc, setDoc } from "firebase/firestore";
import { db } from "../../firebase";
import { useTranslation, Trans } from "react-i18next";
import Swal from "sweetalert2";
import { Spin } from "antd";
import {
  MessageCircle,
  Check,
  Crown,
  Zap,
  Phone,
  Star,
  Gift,
  Lock,
  Unlock,
} from "lucide-react";
import foorchatPlansComparison from "../../constants/foorchatPlansComparison.json";

function Foorchat() {
  const [showSideBar, setShowSideBar] = useState(true);
  const store = useSelector((state) => state.StoreReducer);
  const [isCheckingStatus, setIsCheckingStatus] = useState(true);
  const account = useSelector((state) => state.AccountReducer);

  const { t, i18n } = useTranslation();
  const isRTL = i18n.language === "ar";

  // states for Foorchat activation
  const [isActivating, setIsActivating] = useState(false);
  const [isFoorchatActivated, setIsFoorchatActivated] = useState(false);

  const getIconComponent = (iconName, size = 18) => {
    const icons = {
      Star: <Star size={size} />,
      Zap: <Zap size={size} />,
      Crown: <Crown size={24} />,
    };
    return icons[iconName];
  };

  const plans = useMemo(() => {
    return (foorchatPlansComparison?.plans || []).map((plan) => ({
      id: plan.id,
      name: t(plan.nameKey) !== plan.nameKey ? t(plan.nameKey) : plan.id,
      price: plan.price,
      period:
        t(plan.periodKey) !== plan.periodKey
          ? t(plan.periodKey)
          : plan.periodKey,
      icon: getIconComponent(plan.icon),
      color: plan.color,
      borderColor: plan.borderColor,
      bgColor: plan.bgColor,
      textColor: plan.textColor,
      available: plan.available === true,
      popular: plan.popular === true,
      foorchatIncluded: plan.foorchatIncluded === true,
      features: (plan.features || []).map((featureKey) => {
        const translated = t(featureKey);
        return translated !== featureKey
          ? translated
          : featureKey.replace(/_/g, " ");
      }),
    }));
  }, [t]);

  // add free 5 dollars for the first time (same code as in ActivationFoorchat)
  const addInitialBalance = async () => {
    try {
      // check if there is an existing foorchatSolde in the account
      const hasExistingBalance = account?.foorchatSolde?.solde !== undefined;

      if (!hasExistingBalance) {
        // add free 5 dollars for the first time
        const accountRef = doc(db, "account", account.id);

        await updateDoc(accountRef, {
          "foorchatSolde.solde": 5.0,
          "foorchatSolde.lastUsed": serverTimestamp(),
          "foorchatSolde.initialBalanceAdded": true, // make sure not to add it another time
          "foorchatSolde.initialBalanceDate": serverTimestamp(),
        });

        return true; // free 5 dollars has been added
      }
      return false;
    } catch (error) {
      console.error("Error adding initial balance:", error);
      return false;
    }
  };

  // handle Foorchat activation 
  const handleFoorchatActivation = async () => {
    if (!store?.id || !account?.id) {
      await Swal.fire({
        icon: "error",
        title: t("foorchat.errors.title"),
        text: t("foorchat.errors.text"),
        confirmButtonText: t("foorchat.errors.activation_confirm_button"),
        confirmButtonColor: "rgb(147, 51, 234)",
      });
      return;
    }

    setIsActivating(true);

    try {
      const updatedStoreData = {
        ...store,
        foorchat: {
          ...store.foorchat,
          enabled: true,
        },
      };

      await setDoc(doc(db, "store", store.id), updatedStoreData, {
        merge: true,
      });

      const balanceAdded = await addInitialBalance();

      setIsActivating(false);

      await Swal.fire({
        icon: "success",
        title: t("foorchat.success.title"),
        html: `
          <div class="text-center">
            <div class="mb-4">
              <div class="inline-flex items-center justify-center w-16 h-16 bg-gradient-to-r from-purple-500 to-pink-500 rounded-full mb-4">
                <svg class="w-8 h-8 text-white" fill="currentColor" viewBox="0 0 20 20">
                  <path d="M4 4a2 2 0 00-2 2v1h16V6a2 2 0 00-2-2H4zM18 9H2v5a2 2 0 002 2h12a2 2 0 002-2V9zM4 13a1 1 0 011-1h1a1 1 0 110 2H5a1 1 0 01-1-1zm5-1a1 1 0 100 2h1a1 1 0 100-2H9z"></path>
                </svg>
              </div>
            </div>
            <p class="text-lg text-gray-700 mb-2">${t("foorchat.success.activation")}</p>
            ${
              balanceAdded
                ? `<p class="text-lg text-gray-700 mb-2"> ${t("foorchat.success.added_balance")} <strong class="text-purple-600">$5.00</strong> ${t("foorchat.success.added_balance_to")}</p>`
                : ""
            }
            <p class="text-sm text-gray-500"> ${t("foorchat.success.foorchat_usage")} </p>
          </div>
        `,
        confirmButtonText: t("foorchat.success.confirm_button"),
        confirmButtonColor: "rgb(147, 51, 234)",
        customClass: {
          popup: "rounded-2xl",
          confirmButton: "rounded-xl px-8 py-3",
        },
      });

      setIsFoorchatActivated(true);
    } catch (error) {
      console.error("Error activating Foorchat:", error);
      setIsActivating(false);

      await Swal.fire({
        icon: "error",
        title: t("foorchat.errors.activation_title"),
        text: t("foorchat.errors.activation_text"),
        confirmButtonText: t("foorchat.errors.confirm_button"),
        confirmButtonColor: "rgb(147, 51, 234)",
      });
    }
  };

  useEffect(() => {
    if (!store?.id) {
      // we're still waiting for Redux to load
      setIsCheckingStatus(true);
      return;
    }

    setIsFoorchatActivated(!!store?.foorchat?.enabled);
    setIsCheckingStatus(false);
  }, [store?.id, store?.foorchat?.enabled]);

  // render plans page when Foorchat isn't active
  const renderPlansPage = () => {
    const currentUserPlan = account?.subscription?.plan;

    return (
      <div className="min-h-screen bg-gradient-to-br from-gray-50 via-white to-purple-50 p-6">
        <div className="max-w-7xl mx-auto">
          {/* Header */}
          <div className="text-center mb-12">
            <div className="inline-flex items-center justify-center w-20 h-20 bg-gradient-to-r from-purple-500 to-pink-500 rounded-full mb-6 shadow-2xl">
              <MessageCircle className="w-10 h-10 text-white" />
            </div>

            <h1 className="text-4xl font-bold bg-gradient-to-r from-purple-600 to-pink-600 bg-clip-text text-transparent mb-4">
              {t("foorchat.discover.title")}
            </h1>

            <p className="text-xl text-gray-600 max-w-3xl mx-auto leading-relaxed">
              {t("foorchat.discover.description")}
            </p>

            {/* Foorchat's features */}
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mt-8 max-w-4xl mx-auto">
              <div className="bg-white rounded-xl p-6 shadow-lg border border-purple-100">
                <div className="w-12 h-12 bg-gradient-to-r from-green-500 to-emerald-500 rounded-full flex items-center justify-center mb-4 mx-auto">
                  <MessageCircle className="w-6 h-6 text-white" />
                </div>
                <h3 className="font-bold text-gray-800 mb-2">
                  {" "}
                  {t("foorchat.discover.features.smartChats.title")}{" "}
                </h3>
                <p className="text-gray-600 text-sm">
                  {t("foorchat.discover.features.smartChats.description")}
                </p>
              </div>

              <div className="bg-white rounded-xl p-6 shadow-lg border border-purple-100">
                <div className="w-12 h-12 bg-gradient-to-r from-blue-500 to-cyan-500 rounded-full flex items-center justify-center mb-4 mx-auto">
                  <Zap className="w-6 h-6 text-white" />
                </div>
                <h3 className="font-bold text-gray-800 mb-2">
                  {" "}
                  {t("foorchat.discover.features.instantResponse.title")}{" "}
                </h3>
                <p className="text-gray-600 text-sm">
                  {t("foorchat.discover.features.instantResponse.description")}
                </p>
              </div>

              <div className="bg-white rounded-xl p-6 shadow-lg border border-purple-100">
                <div className="w-12 h-12 bg-gradient-to-r from-purple-500 to-pink-500 rounded-full flex items-center justify-center mb-4 mx-auto">
                  <Crown className="w-6 h-6 text-white" />
                </div>
                <h3 className="font-bold text-gray-800 mb-2">
                  {" "}
                  {t("foorchat.discover.features.salesIncrease.title")}{" "}
                </h3>
                <p className="text-gray-600 text-sm">
                  {t("foorchat.discover.features.salesIncrease.description")}
                </p>
              </div>
            </div>
          </div>

          {/* Plans Grid */}
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-8 mb-12">
            {plans.map((plan) => {
              const isCurrentPlan = currentUserPlan === plan.id;
              const canActivate = plan.available && plan.foorchatIncluded;

              return (
                <div
                  key={plan.id}
                  className={`relative bg-white rounded-2xl shadow-xl border-2 transition-all duration-300 hover:scale-105 ${
                    plan.popular
                      ? "border-purple-300 ring-4 ring-purple-100"
                      : plan.borderColor
                  } ${!plan.available ? "opacity-75" : ""}`}
                >
                  {/* Popular Badge */}
                  {plan.popular && (
                    <div className="absolute -top-4 left-1/2 transform -translate-x-1/2 z-10">
                      <div className="bg-gradient-to-r from-purple-500 to-pink-500 text-white px-6 py-2 rounded-full text-sm font-bold shadow-lg">
                        {t("foorchat.plans.popular")}
                      </div>
                    </div>
                  )}

                  {/* Coming Soon Badge */}
                  {!plan.available && (
                    <div className="absolute -top-4 right-4 z-10">
                      <div className="bg-gradient-to-r from-gray-500 to-gray-600 text-white px-4 py-2 rounded-full text-xs font-bold shadow-lg">
                        {t("foorchat.plans.availability")}
                      </div>
                    </div>
                  )}

                  <div
                    className={`bg-gradient-to-r ${plan.bgColor} p-6 rounded-t-2xl`}
                  >
                    <div
                      className={`inline-flex items-center justify-center w-16 h-16 bg-gradient-to-r ${plan.color} rounded-full mb-4 shadow-lg text-white`}
                    >
                      {plan.icon}
                    </div>

                    <h3 className="text-2xl font-bold text-gray-800 mb-2">
                      {plan.name}
                    </h3>

                    <div className="flex items-baseline mb-4">
                      <span className="text-4xl font-bold text-gray-900">
                        ${plan.price}
                      </span>
                      <span className="text-gray-600 ms-2">/{plan.period}</span>
                    </div>
                  </div>

                  <div className="p-6">
                    {/* Features */}
                    <ul className="space-y-3 mb-6">
                      {plan.features.map((feature, index) => (
                        <li
                          key={index}
                          className="flex items-center text-gray-700"
                        >
                          <div
                            className={`w-5 h-5 rounded-full bg-gradient-to-r ${plan.color} flex items-center justify-center me-3 flex-shrink-0`}
                          >
                            <Check className="w-3 h-3 text-white" />
                          </div>
                          <span className="text-sm">{feature}</span>
                        </li>
                      ))}
                    </ul>

                    {/* Action Button */}
                    <div className="space-y-3">
                      {isCurrentPlan && canActivate ? (
                        <button
                          onClick={handleFoorchatActivation}
                          disabled={isActivating}
                          className={`w-full py-4 rounded-xl font-bold text-white shadow-lg transition-all duration-300 transform hover:scale-105 ${
                            isActivating
                              ? "bg-gray-400 cursor-not-allowed"
                              : `bg-gradient-to-r ${plan.color} hover:shadow-xl`
                          }`}
                        >
                          {isActivating ? (
                            <div className="flex items-center justify-center gap-2">
                              <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-white" />
                              {t("foorchat.plans.activation.activating")}
                            </div>
                          ) : (
                            <div className="flex items-center justify-center gap-2">
                              <Unlock className="w-5 h-5" />
                              {t("foorchat.plans.activation.free_activation")}
                              <Gift className="w-5 h-5 text-yellow-300" />
                            </div>
                          )}
                        </button>
                      ) : !plan.available ? (
                        <button
                          disabled
                          className="w-full py-4 rounded-xl font-bold bg-gray-300 text-gray-500 cursor-not-allowed"
                        >
                          <div className="flex items-center justify-center gap-2">
                            <Lock className="w-5 h-5" />
                            {t("foorchat.plans.activation.availability")}
                          </div>
                        </button>
                      ) : !isCurrentPlan ? (
                        <button
                          className={`w-full py-4 rounded-xl font-bold text-white shadow-lg transition-all duration-300 transform hover:scale-105 bg-gradient-to-r ${plan.color} hover:shadow-xl`}
                        >
                          {t("foorchat.plans.activation.upgrade")} {plan.name}
                        </button>
                      ) : (
                        <div className="text-center py-4">
                          <span className="text-green-600 font-bold">
                            {t("foorchat.plans.activation.current_plan")}
                          </span>
                        </div>
                      )}

                      {/* Free $5 Notice – only Basic */}
                      {plan.id === "الاساسية" &&
                        canActivate &&
                        isCurrentPlan && (
                          <div className="bg-gradient-to-r from-green-50 to-emerald-50 border border-green-200 rounded-xl p-4">
                            <div className="flex items-center justify-center gap-2 text-center">
                              <Gift className="w-6 h-6 text-green-500 flex-shrink-0" />
                              <div>
                                <div className="font-bold text-green-800 text-sm">
                                  {t("foorchat.plans.activation.gift")}
                                </div>
                                <div className="text-green-600 text-xs">
                                  <Trans
                                    i18nKey="foorchat.plans.activation.get_free_balance"
                                    values={{ amount: "$5.00" }}
                                    components={{ strong: <strong /> }}
                                  />
                                </div>
                              </div>
                            </div>
                          </div>
                        )}
                    </div>
                  </div>
                </div>
              );
            })}
          </div>

          {/* FAQ Section */}
          <div className="bg-white rounded-2xl shadow-xl p-8 border border-gray-100">
            <h2 className="text-2xl font-bold text-center text-gray-800 mb-8">
              {t("foorchat.faq.title")}
            </h2>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div className="space-y-4">
                <div>
                  <h4 className="font-bold text-gray-800 mb-2">
                    {t("foorchat.faq.whatIs.title")}
                  </h4>
                  <p className="text-gray-600 text-sm">
                    {t("foorchat.faq.whatIs.answer")}
                  </p>
                </div>

                <div>
                  <h4 className="font-bold text-gray-800 mb-2">
                    {t("foorchat.faq.howItWorks.title")}
                  </h4>
                  <p className="text-gray-600 text-sm">
                    {t("foorchat.faq.howItWorks.answer")}
                  </p>
                </div>
              </div>

              <div className="space-y-4">
                <div>
                  <h4 className="font-bold text-gray-800 mb-2">
                    {t("foorchat.faq.whyFree.title")}
                  </h4>
                  <p className="text-gray-600 text-sm">
                    {t("foorchat.faq.whyFree.answer")}
                  </p>
                </div>

                <div>
                  <h4 className="font-bold text-gray-800 mb-2">
                    {t("foorchat.faq.canCancel.title")}
                  </h4>
                  <p className="text-gray-600 text-sm">
                    {t("foorchat.faq.canCancel.answer")}
                  </p>
                </div>
              </div>
            </div>
          </div>

          {/* Call to Action */}
          <div className="text-center mt-12 bg-gradient-to-r from-purple-600 to-pink-600 rounded-2xl p-8 text-white">
            <h2 className="text-3xl font-bold mb-4">
              {t("foorchat.cta.title")}
            </h2>
            <p className="text-xl opacity-90 mb-6">
              {t("foorchat.cta.description")}
            </p>

            {currentUserPlan === "الاساسية" && (
              <div className="bg-white/20 rounded-xl p-4 inline-block">
                <p className="text-sm">
                  <Trans
                    i18nKey="foorchat.cta.currentPlan"
                    values={{ plan: t("foorchat.plans.basic.name") }}
                    components={{ strong: <strong /> }}
                  />
                </p>
                <p className="text-xs opacity-75">
                  {t("foorchat.cta.canActivateWithFree")}
                </p>
              </div>
            )}
          </div>
        </div>
      </div>
    );
  };

  return (
    <div
      className="container-fluid bg-purple-50 dark:!bg-[#141414] min-h-screen"
      dir={isRTL ? "rtl" : "ltr"}
    >
      <div className="row">
        {showSideBar && (
          <div
            style={{
              width: "250px",
              flexShrink: 0,
              transition: "width 0.6s ease",
              boxShadow: "2px 0 10px rgba(0,0,0,0.05)",
            }}
          >
            <Sidebar
              showSideBar={showSideBar}
              setShowSideBar={setShowSideBar}
            />
          </div>
        )}
        <div
          className="px-3 dark:!bg-[#141414]"
          style={{ flex: 1, backgroundColor: "#fbf8fc" }}
        >
          <Navbar showSideBar={showSideBar} setShowSideBar={setShowSideBar} />

          {/* Conditional Rendering: Plans Page or Tabs */}
          {
            isCheckingStatus ? (
              <div className="flex items-center justify-center min-h-[60vh]">
                <Spin size="large" />
              </div>
            ) : isFoorchatActivated ? (
              <FoorchatDashboard
                onDeactivate={() => setIsFoorchatActivated(false)}
              />
            ) : (
              renderPlansPage()
            ) 
          }
          <Footer />
        </div>
      </div>
    </div>
  );
}

export default Foorchat;
